import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, where, limit, onSnapshot } from 'firebase/firestore';
import {
  RefreshCw, Sparkles, Mail, Calendar, FolderKanban, ArrowUpRight, Loader2, Check, ListTodo, Factory,
} from 'lucide-react';
import {
  ExecutiveDayBriefing, BriefingPrioritySource, ExecInboxThread, OpsTask, ProjectTask,
  ExecutiveBriefingProfile, CRMUser, TimberProject,
} from '../../types';
import { db } from '../../utils/firebase';
import {
  subscribeDayBriefing, refreshDayBriefing, fetchDayBriefing, dismissBriefingPriority, todayBriefingDateKey,
} from '../../utils/executiveBriefingApi';
import {
  briefingPriorityKey, filterActiveBriefingPriorities,
} from '../../utils/briefingPriorityResolution';

interface Props {
  ownerUid: string;
  ownerName: string;
  profile?: ExecutiveBriefingProfile;
  briefingOnly?: boolean;
  crmUsers?: CRMUser[];
  timberProjects?: TimberProject[];
  inboxThreads: ExecInboxThread[];
  projectTasks: ProjectTask[];
  onToast: (msg: string, error?: boolean) => void;
  onOpenInboxThread?: (threadId: string) => void;
  onOpenCalendar?: () => void;
}

function fmtTime(d: Date): string {
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function fmtDateLabel(dateKey: string): string {
  const [y, m, d] = dateKey.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
}

function sourceIcon(source: BriefingPrioritySource) {
  switch (source) {
    case 'email': return <Mail size={13} />;
    case 'calendar': return <Calendar size={13} />;
    case 'projectHub': return <FolderKanban size={13} />;
    case 'ops': return <ListTodo size={13} />;
    case 'production': return <Factory size={13} />;
    default: return <Sparkles size={13} />;
  }
}

function sourceLabel(source: BriefingPrioritySource): string {
  switch (source) {
    case 'email': return 'Email';
    case 'calendar': return 'Calendar';
    case 'projectHub': return 'Project Hub';
    case 'ops': return 'OPS';
    case 'production': return 'Production';
    default: return 'Priority';
  }
}

function renderNarrative(text: string): React.ReactNode {
  const blocks = text.split(/\n\n+/);
  return blocks.map((block, i) => {
    const trimmed = block.trim();
    if (!trimmed) return null;
    if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
      const items = trimmed.split('\n').filter((l) => /^[-*]\s/.test(l));
      return (
        <ul key={i} className="exec-briefing-list">
          {items.map((line, j) => (
            <li key={j} dangerouslySetInnerHTML={{ __html: inlineMarkdown(line.replace(/^[-*]\s/, '')) }} />
          ))}
        </ul>
      );
    }
    return (
      <p
        key={i}
        className="exec-briefing-para"
        dangerouslySetInnerHTML={{ __html: inlineMarkdown(trimmed) }}
      />
    );
  });
}

function inlineMarkdown(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>');
}

function PriorityLink({
  item,
  onOpenInboxThread,
  onOpenCalendar,
}: {
  item: ExecutiveDayBriefing['priorities'][0];
  onOpenInboxThread?: (threadId: string) => void;
  onOpenCalendar?: () => void;
}) {
  const isExternal = item.href.startsWith('http');
  const isInbox = item.source === 'email' && item.entityId && onOpenInboxThread;
  const isCal = item.source === 'calendar' && onOpenCalendar;

  if (isInbox) {
    return (
      <button
        type="button"
        className="exec-briefing-priority-link"
        onClick={() => onOpenInboxThread!(item.entityId!)}
      >
        Open <ArrowUpRight size={12} />
      </button>
    );
  }

  if (isCal) {
    return (
      <button type="button" className="exec-briefing-priority-link" onClick={onOpenCalendar}>
        Open calendar <ArrowUpRight size={12} />
      </button>
    );
  }

  if (isExternal) {
    return (
      <a href={item.href} className="exec-briefing-priority-link" target="_blank" rel="noopener noreferrer">
        Open <ArrowUpRight size={12} />
      </a>
    );
  }

  return (
    <Link to={item.href} className="exec-briefing-priority-link">
      Open <ArrowUpRight size={12} />
    </Link>
  );
}

const DayBriefingPanel: React.FC<Props> = ({
  ownerUid, ownerName, profile = 'ceo', briefingOnly = false,
  crmUsers = [], timberProjects = [],
  inboxThreads, projectTasks, onToast, onOpenInboxThread, onOpenCalendar,
}) => {
  const dateKey = todayBriefingDateKey();
  const [briefing, setBriefing] = useState<ExecutiveDayBriefing | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dismissingKey, setDismissingKey] = useState<string | null>(null);
  const [opsTasks, setOpsTasks] = useState<OpsTask[]>([]);

  const excludeActionRequiredUids = useMemo(
    () => (profile === 'coo'
      ? crmUsers.filter((u) => u.execInbox?.enabled).map((u) => u.uid)
      : []),
    [crmUsers, profile],
  );

  const closedProjectIds = useMemo(
    () => new Set(
      profile === 'coo'
        ? timberProjects.filter((p) => p.status === 'completed' && p.id).map((p) => p.id as string)
        : [],
    ),
    [timberProjects, profile],
  );

  useEffect(() => {
    if (!ownerUid) return;
    const q = profile === 'coo'
      ? query(collection(db, 'operationsTasks'), limit(150))
      : query(
        collection(db, 'operationsTasks'),
        where('assignedTo', 'array-contains', ownerUid),
        limit(100),
      );
    return onSnapshot(q, (snap) => {
      setOpsTasks(snap.docs.map((d) => {
        const data = d.data();
        return {
          ...data,
          id: d.id,
          dueDate: data.dueDate?.toDate?.() || undefined,
          createdAt: data.createdAt?.toDate?.() || new Date(),
          updatedAt: data.updatedAt?.toDate?.() || new Date(),
        } as OpsTask;
      }));
    });
  }, [ownerUid, profile]);

  useEffect(() => {
    if (!ownerUid) return;
    setLoading(true);
    return subscribeDayBriefing(
      ownerUid,
      dateKey,
      (b) => { setBriefing(b); setLoading(false); },
      (e) => { onToast(e.message, true); setLoading(false); },
    );
  }, [ownerUid, dateKey, onToast]);

  const activePriorities = useMemo(() => {
    if (!briefing) return [];
    return filterActiveBriefingPriorities(briefing.priorities, {
      ownerUid,
      projectTasks,
      inboxThreads,
      opsTasks,
      dismissedKeys: briefing.dismissedPriorityKeys,
      excludeActionRequiredUids,
      closedProjectIds,
    });
  }, [briefing, ownerUid, projectTasks, inboxThreads, opsTasks, excludeActionRequiredUids, closedProjectIds]);

  async function handleRefresh() {
    setRefreshing(true);
    try {
      const b = profile === 'coo'
        ? await fetchDayBriefing(ownerUid, { dateKey })
        : await refreshDayBriefing(ownerUid, { dateKey });
      if (!b) {
        onToast(
          profile === 'coo'
            ? 'No briefing published yet. The COO agent publishes via cooBriefingAgentApi.'
            : 'Briefing could not be saved. Try again.',
          true,
        );
        return;
      }
      setBriefing(b);
      onToast(profile === 'coo' ? 'Briefing loaded' : 'Briefing generated');
    } catch (e) {
      onToast((e as Error).message, true);
    } finally {
      setRefreshing(false);
    }
  }

  async function handleDismiss(priorityKey: string) {
    setDismissingKey(priorityKey);
    try {
      await dismissBriefingPriority(ownerUid, priorityKey, { dateKey });
    } catch (e) {
      onToast((e as Error).message, true);
    } finally {
      setDismissingKey(null);
    }
  }

  if (loading) {
    return (
      <div className="exec-briefing exec-briefing-loading">
        <Loader2 size={20} className="exec-briefing-spin" />
        <span>Loading briefing…</span>
      </div>
    );
  }

  if (!briefing) {
    return (
      <div className="exec-briefing exec-briefing-empty">
        <Sparkles size={28} style={{ color: 'var(--pm-text-muted)' }} />
        <h3>No briefing yet for today</h3>
        <p>
          {briefingOnly
            ? 'The COO agent gathers OPS, Project Hub, and production context via the API, then publishes your daily briefing. Check back after the morning run.'
            : `The executive agent publishes a daily briefing, or you can generate one now from ${ownerName.replace(' (me)', '')}'s email, calendar, and Project Hub tasks.`}
        </p>
        <button type="button" className="exec-cta" onClick={handleRefresh} disabled={refreshing}>
          {refreshing ? <Loader2 size={14} className="exec-briefing-spin" /> : <RefreshCw size={14} />}
          {briefingOnly ? 'Check for briefing' : 'Generate briefing'}
        </button>
      </div>
    );
  }

  const sections = briefing.sections || {};
  const totalPriorities = briefing.priorities.length;
  const completedCount = totalPriorities - activePriorities.length;

  return (
    <div className="exec-briefing">
      <div className="exec-briefing-header">
        <div>
          <h3 className="exec-briefing-date">{fmtDateLabel(briefing.dateKey)}</h3>
          <p className="exec-briefing-meta">
            Generated {fmtTime(briefing.generatedAt)} by {briefing.generatedBy === 'agent' ? (briefingOnly ? 'COO Agent' : 'Executive Agent') : 'CRM'}
            {completedCount > 0 && (
              <> · {completedCount} of {totalPriorities} priorities done</>
            )}
          </p>
        </div>
        <button
          type="button"
          className="exec-icon-btn"
          onClick={handleRefresh}
          disabled={refreshing}
          title="Refresh briefing"
        >
          {refreshing ? <Loader2 size={13} className="exec-briefing-spin" /> : <RefreshCw size={13} />}
          Refresh
        </button>
      </div>

      <div className="exec-briefing-chips">
        {sections.email != null && (
          <span className="exec-briefing-chip">
            <Mail size={11} />
            {sections.email.summary || `${sections.email.itemCount ?? 0} email items`}
          </span>
        )}
        {sections.calendar != null && (
          <span className="exec-briefing-chip">
            <Calendar size={11} />
            {sections.calendar.summary || `${sections.calendar.itemCount ?? 0} events`}
          </span>
        )}
        {sections.projectHub != null && (
          <span className="exec-briefing-chip">
            <FolderKanban size={11} />
            {sections.projectHub.summary || `${sections.projectHub.itemCount ?? 0} tasks`}
          </span>
        )}
        {sections.ops != null && (
          <span className="exec-briefing-chip">
            <ListTodo size={11} />
            {sections.ops.summary || `${sections.ops.itemCount ?? 0} OPS tasks`}
          </span>
        )}
        {sections.production != null && (
          <span className="exec-briefing-chip">
            <Factory size={11} />
            {sections.production.summary || `${sections.production.itemCount ?? 0} production`}
          </span>
        )}
      </div>

      <div className="exec-briefing-narrative">
        {renderNarrative(briefing.narrative)}
      </div>

      {totalPriorities > 0 && (
        <div className="exec-briefing-priorities">
          <h4 className="exec-briefing-priorities-title">Top priorities</h4>
          {activePriorities.length === 0 ? (
            <div className="exec-briefing-all-done">
              <Check size={18} />
              <span>All priorities complete for today</span>
            </div>
          ) : (
            <div className="exec-briefing-priority-list">
              {activePriorities.map((item) => {
                const key = briefingPriorityKey(item);
                return (
                  <div key={key} className="exec-briefing-priority-card">
                    <div className="exec-briefing-priority-rank">{item.rank}</div>
                    <div className="exec-briefing-priority-body">
                      <div className="exec-briefing-priority-top">
                        <span className={`exec-briefing-priority-source source-${item.source}`}>
                          {sourceIcon(item.source)}
                          {sourceLabel(item.source)}
                        </span>
                        <div className="exec-briefing-priority-actions">
                          <button
                            type="button"
                            className="exec-briefing-done-btn"
                            onClick={() => handleDismiss(key)}
                            disabled={dismissingKey === key}
                            title="Mark done"
                          >
                            {dismissingKey === key
                              ? <Loader2 size={11} className="exec-briefing-spin" />
                              : <Check size={11} />}
                            Done
                          </button>
                          <PriorityLink
                            item={item}
                            onOpenInboxThread={onOpenInboxThread}
                            onOpenCalendar={onOpenCalendar}
                          />
                        </div>
                      </div>
                      <div className="exec-briefing-priority-title">{item.title}</div>
                      <div className="exec-briefing-priority-rationale">{item.rationale}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DayBriefingPanel;
